import { Matrix, MatrixVector } from '../math';
/**
 * Represents a translation transformation with x and y offset values.
 * Provides methods to convert to matrix form and perform vector operations.
 */
export class Translation {
    /**
     * Creates a new Translation transformation.
     * @param x The x offset
     * @param y The y offset
     */
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    /**
     * Converts the translation to a matrix vector representation.
     * @returns A MatrixVector representing the translation
     */
    toMatrixVector() {
        return new MatrixVector(0, 0, 0, 0, this.x, this.y);
    }
    /**
     * Converts the translation transformation to a matrix representation.
     * @returns A matrix representing the translation transformation
     */
    toMatrix() {
        return Matrix.translation(this.x, this.y);
    }
    /**
     * Returns a string representation of the translation transformation.
     * @returns String representation in CSS transform format
     */
    toString() {
        return `translate(${this.x},${this.y})`;
    }
    /**
     * Adds another translation to this one.
     * @param delta The translation to add
     */
    plus(delta) {
        this.x += delta.x;
        this.y += delta.y;
    }
    /**
     * Multiplies the translation by a scaling factor.
     * @param factor The scaling factor to apply
     */
    times(factor) {
        this.x *= factor;
        this.y *= factor;
    }
    /**
     * Calculates the length (magnitude) of the translation vector.
     * @returns The length of the translation vector
     */
    length() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }
    /**
     * Normalizes the translation vector to unit length.
     */
    normalize() {
        if (this.Length === 0) {
            return;
        }
        this.times(1 / this.length());
    }
    /**
     * Creates an inverted version of this translation transformation.
     * @returns A new Translation with inverted offsets
     */
    invert() {
        return new Translation(-this.x, -this.y);
    }
}
